import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { Input } from "@/components/ui/input";
import { MoneyAmountInput } from "@/components/sales/MoneyAmountInput";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  defaultFirstInstallmentDueDate,
  INSTALLMENT_MONTH_OPTIONS,
  installmentStatusClass,
  installmentStatusLabel,
  installmentsMatchBalance,
  sumInstallmentAmounts,
  type SaleInstallmentDraft,
} from "@/lib/sales/saleInstallments";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo } from "react";

type Props = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  hideToggle?: boolean;
  balanceAmount: number;
  saleDate: string;
  payableDate: string;
  monthCount: number;
  onMonthCountChange: (count: number) => void;
  firstDueDate: string;
  onFirstDueDateChange: (iso: string) => void;
  installments: SaleInstallmentDraft[];
  onInstallmentAmountChange: (installmentNo: number, amount: number) => void;
  disabled?: boolean;
};

function money(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function SaleInstallmentPlanner({
  enabled,
  onEnabledChange,
  hideToggle = false,
  balanceAmount,
  saleDate,
  payableDate,
  monthCount,
  onMonthCountChange,
  firstDueDate,
  onFirstDueDateChange,
  installments,
  onInstallmentAmountChange,
  disabled,
}: Props) {
  const canPlan = balanceAmount > 0;
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const installmentTotal = useMemo(() => sumInstallmentAmounts(installments), [installments]);
  const totalsMatch = useMemo(
    () => installmentsMatchBalance(installments, balanceAmount, 0),
    [installments, balanceAmount],
  );
  const remainingToAllocate = useMemo(
    () => Math.round((balanceAmount - installmentTotal) * 100) / 100,
    [balanceAmount, installmentTotal],
  );

  useEffect(() => {
    if (!enabled || !canPlan) return;
    if (!firstDueDate.trim()) {
      onFirstDueDateChange(defaultFirstInstallmentDueDate(saleDate, payableDate));
    }
  }, [enabled, canPlan, firstDueDate, saleDate, payableDate, onFirstDueDateChange]);

  return (
    <div className="space-y-4 rounded-lg border border-border/50 bg-muted/10 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">Installment schedule</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Split the balance ({money(balanceAmount)}) into monthly payments. Edit amounts as needed.
          </p>
        </div>
        {!hideToggle ? (
          <Switch
            checked={enabled}
            onCheckedChange={onEnabledChange}
            disabled={disabled || !canPlan}
            aria-label="Enable installment plan"
          />
        ) : null}
      </div>

      {!canPlan ? (
        <p className="text-xs text-muted-foreground">Enter a sale price and token so the balance is greater than zero.</p>
      ) : null}

      {enabled && canPlan ? (
        <>
          <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-2 lg:col-span-2">
              <Label className="text-xs font-medium text-muted-foreground">Number of installments</Label>
              <div className="flex flex-wrap items-center gap-2">
                {INSTALLMENT_MONTH_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={disabled}
                    onClick={() => onMonthCountChange(n)}
                    className={cn(
                      "h-9 min-w-[2.5rem] rounded-lg border px-3 text-xs font-semibold transition-colors shadow-xs",
                      monthCount === n
                        ? "border-primary bg-primary text-primary-foreground shadow-sm"
                        : "border-border/60 bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    {n}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={120}
                  disabled={disabled}
                  value={
                    INSTALLMENT_MONTH_OPTIONS.includes(monthCount as (typeof INSTALLMENT_MONTH_OPTIONS)[number])
                      ? ""
                      : monthCount
                  }
                  placeholder="Custom"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v) && v >= 1) onMonthCountChange(Math.min(120, Math.floor(v)));
                  }}
                  className="glass-input h-9 w-20 px-2 text-xs tabular-nums"
                />
              </div>
            </div>

            <div className="space-y-2 max-w-xs">
              <Label className="text-xs font-medium text-muted-foreground">First installment due date</Label>
              <DatePartsPicker
                value={firstDueDate}
                onChange={onFirstDueDateChange}
                fromYear={currentYear}
                toYear={currentYear + 15}
                placeholder="Select first due date"
                disabled={disabled}
              />
            </div>
          </div>

          {installments.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm w-full">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="border-b border-border/40 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      <th className="w-12 px-3 py-1.5 text-center">#</th>
                      <th className="px-3 py-1.5 text-left">Due date</th>
                      <th className="w-64 px-3 py-1.5 text-right">Amount</th>
                      <th className="w-32 px-3 py-1.5 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/20">
                    {installments.map((row) => {
                      const due = parseISO(row.due_date);
                      const dueLabel = isValid(due) ? format(due, "PP") : row.due_date;
                      return (
                        <tr key={row.installment_no} className="hover:bg-muted/20 transition-colors">
                          <td className="px-3 py-1.5 text-center font-medium tabular-nums text-muted-foreground text-xs">
                            {row.installment_no}
                          </td>
                          <td className="px-3 py-1.5 font-medium text-foreground text-xs">
                            {dueLabel}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <div className="flex items-center justify-end">
                              <MoneyAmountInput
                                value={row.amount}
                                size="sm"
                                disabled={disabled}
                                inputClassName="w-36 sm:w-44 h-8 text-xs font-semibold tabular-nums px-2.5 bg-background shadow-xs border-border/60"
                                onChange={(amount) => onInstallmentAmountChange(row.installment_no, amount)}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold tracking-wide",
                                installmentStatusClass("pending"),
                              )}
                            >
                              {installmentStatusLabel("pending")}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-border/40 bg-muted/30 font-semibold text-xs">
                      <td colSpan={2} className="px-3 py-1.5 text-muted-foreground">
                        Installment total ({installments.length})
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-bold text-foreground">
                        {formatCurrency(installmentTotal)}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold tracking-wide",
                            totalsMatch
                              ? "border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                              : "border border-destructive/30 bg-destructive/15 text-destructive",
                          )}
                        >
                          {totalsMatch ? "Matches balance" : "Mismatch"}
                        </span>
                      </td>
                    </tr>
                    {!totalsMatch ? (
                      <tr className="bg-amber-500/10">
                        <td colSpan={4} className="px-3 py-1.5 text-xs text-muted-foreground">
                          Balance: <span className="font-semibold text-foreground">{formatCurrency(balanceAmount)}</span>
                          {" · "}
                          {remainingToAllocate > 0 ? (
                            <>
                              Remaining to allocate:{" "}
                              <span className="font-bold text-amber-700 dark:text-amber-400">{formatCurrency(remainingToAllocate)}</span>
                            </>
                          ) : (
                            <>
                              Over allocated by:{" "}
                              <span className="font-bold text-destructive">{formatCurrency(Math.abs(remainingToAllocate))}</span>
                            </>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
